import {Button, Group, Loader, Paper, Stack, Text, Title} from '@mantine/core'
import {Link} from '@tanstack/react-router'

import type {ProjectBillingTerms} from '../projects/schemas'
import {useProjectBillingTerms} from '../projects/use-projects'
import {
    getActiveProjectBillingTerms,
    getProjectBillingTermsDisplayLines,
} from './billing-terms-display'

type ProjectBillingTermsReadViewProps = {
    isLoading: boolean
    isError: boolean
    activeTerms: ProjectBillingTerms | null
}

export function ProjectBillingTermsReadView({
    isLoading,
    isError,
    activeTerms,
}: ProjectBillingTermsReadViewProps) {
    if (isLoading) {
        return <Loader size="sm" />
    }

    if (isError) {
        return (
            <Text size="sm" c="red">
                Could not load billing terms.
            </Text>
        )
    }

    if (activeTerms == null) {
        return (
            <Text size="sm" c="dimmed">
                No billing terms have been set for this order.
            </Text>
        )
    }

    return (
        <Stack gap="xs">
            {getProjectBillingTermsDisplayLines(activeTerms).map((line) => (
                <Text key={line.label} size="sm">
                    {line.label}: {line.value}
                </Text>
            ))}
        </Stack>
    )
}

type ProjectBillingTermsContentProps = {
    projectId: string
}

export function ProjectBillingTermsContent({projectId}: ProjectBillingTermsContentProps) {
    const {data: billingTerms = [], isLoading, isError} = useProjectBillingTerms(projectId)
    const activeTerms = getActiveProjectBillingTerms(billingTerms)

    return (
        <ProjectBillingTermsReadView
            isLoading={isLoading}
            isError={isError}
            activeTerms={activeTerms}
        />
    )
}

type JobBillingTermsSectionProps = {
    projectId: string
}

export function JobBillingTermsSection({projectId}: JobBillingTermsSectionProps) {
    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Title order={3}>Billing terms</Title>
                    <Button
                        component={Link}
                        to="/projects/$projectId"
                        params={{projectId}}
                        variant="subtle"
                        size="compact-sm"
                    >
                        View in order
                    </Button>
                </Group>

                <ProjectBillingTermsContent projectId={projectId} />
            </Stack>
        </Paper>
    )
}
